import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Input, Label } from 'reactstrap';

import apiPdfEnd, { IOfferPdfSettings, OfferOwner } from 'api/offerPdfEnd';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';

type PriceChoice =
  | 'showOfferTotal'
  | 'showPositionPrices'
  | 'showWorkTypeTotals';

// In the order the prices are on the PDF
const CHOICES: PriceChoice[] = [
  'showPositionPrices',
  'showWorkTypeTotals',
  'showOfferTotal',
];

/**
 * Which prices the offer PDF shows: the price of each position, the total of each work type
 * (on the row of its heading) and the total of the whole offer (on a row of its own). At
 * least one is shown. Saved at once.
 */
const OfferPdfPriceChoices = ({
  locked,
  owner,
  ownerId,
}: {
  locked: boolean;
  owner: OfferOwner;
  ownerId: number;
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers]);
  const [settings, setSettings] = useState<IOfferPdfSettings>();
  const [saving, setSaving] = useState(false);
  const tr = (key: string) => t(`${translations.offers}:pdfPrices.${key}`);

  useEffect(() => {
    apiPdfEnd.pdfSettings
      .get(owner, ownerId)
      .then(setSettings)
      .catch(() => setSettings(undefined));
  }, [owner, ownerId]);

  if (settings === undefined) {
    return null;
  }

  const shownCount = CHOICES.filter((choice) => settings[choice]).length;

  const toggle = async (choice: PriceChoice) => {
    const next = { ...settings, [choice]: !settings[choice] };
    setSaving(true);
    setSettings(next);

    try {
      await apiPdfEnd.pdfSettings.patch(owner, ownerId, {
        [choice]: next[choice],
      });
    } catch (error) {
      setSettings(settings);
      dispatch(addMessage(tr('Save failed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="offer-pdf-prices">
      <span className="offer-pdf-prices-title">{tr('Title')}</span>
      {CHOICES.map((choice) => (
        <Label check key={choice} className="offer-pdf-prices-choice">
          <Input
            checked={settings[choice]}
            // The last price shown cannot be taken away
            disabled={
              locked || saving || (settings[choice] && shownCount === 1)
            }
            onChange={() => toggle(choice)}
            type="checkbox"
          />{' '}
          {tr(choice)}
        </Label>
      ))}
    </div>
  );
};

export default OfferPdfPriceChoices;
